import React, { Fragment, RefObject, useCallback, useMemo, useRef, useState } from "react";
import { Parcel } from "../../../model/parcel";
import { Button, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { ParcelDetail } from "../../../model/parcel-detail";
import { useElementVisible } from "../../../../common/hooks/use-element-visible";
import { classNamesList } from "../../../../common/utils/class-name";
import { Add, Deselect, Done, Remove, SelectAll } from "@mui/icons-material";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { t } from "i18next";
import { ParcelStockoutHandler } from "../../../../common/cqrs/command/parcel-stockout/parcel-stockout-handler";
import { ParcelStockoutCommand } from "../../../../common/cqrs/command/parcel-stockout/parcel-stockout-command";

interface IParcelStockoutProductSelectionProps {
    parcel: Parcel;
    hasNext: boolean;
    onParcelCompleted: (parcel: Parcel) => void;
    onCancel: () => void;
}

export const ParcelStockoutProductSelection = (props: IParcelStockoutProductSelectionProps) => {
    const [isStockoutRunning, setIsStockoutRunning] = useState(false);
    const [stockoutData, setStockoutData] = useState<Map<number, number>>(new Map<number, number>());
    const isAllStockoutSelected = useMemo(() =>
            props.parcel.details.every(d => stockoutData.get(d.productId) === d.quantity),
        [stockoutData]);

    const bottomItemRef = useRef<HTMLDivElement>(null);
    const isBottomReached = useElementVisible(bottomItemRef);

    const handleParcelDetailSelected = useCallback((detail: ParcelDetail, stockoutQuantity: number) => {
        setStockoutData(prev => {
            const newMap = new Map(prev);

            if (stockoutQuantity === 0) {
                newMap.delete(detail.productId);
            } else {
                newMap.set(detail.productId, stockoutQuantity);
            }

            return newMap;
        });
    }, []);

    const handleSelectAll = useCallback(() =>
            setStockoutData(new Map(props.parcel.details.map(d => [d.productId, d.quantity]))),
        [props.parcel]);

    const handleDeselectAll = useCallback(() => { setStockoutData(new Map<number, number>()); }, []);

    const handleStockoutExecute = useCallback(async () => {
        try {
            setIsStockoutRunning(true);
            await ParcelStockoutHandler(new ParcelStockoutCommand(props.parcel, stockoutData));
            setIsStockoutRunning(false);

            if (props.parcel.status === "Cancelled") {
                notificationModel.addSuccessMessage(t("components.parcel.markAsStockout.successWithCancellation",
                    { parcelId: props.parcel.id }));
            } else {
                notificationModel.addSuccessMessage(t(`components.parcel.markAsStockout.success${stockoutData.size > 1 ? "Many" : ""}`,
                    { parcelId: props.parcel.id, quantity: stockoutData.size }));
            }

            props.onParcelCompleted(props.parcel);
        } catch (err) {
            notificationModel.addErrorMessage(t("components.parcel.markAsStockout.error",
                { parcelId: props.parcel.parcelId.toString() }));
            setIsStockoutRunning(false);
        }
    }, [props.parcel, stockoutData]);

    return <Fragment>
        <DialogTitle className={"products-selection__title"}>
            <div>
                {t("components.parcel.markAsStockout.form.title", {
                    parcelId: props.parcel.parcelId.toString(),
                })}
            </div>
            {!isAllStockoutSelected ?
                <Button onClick={handleSelectAll} variant={"outlined"} color={"inherit"} startIcon={<SelectAll />}>
                    {t("components.parcel.markAsStockout.form.action.selectAll")}
                </Button> :
                <Button onClick={handleDeselectAll} variant={"outlined"} color={"inherit"} startIcon={<Deselect />}>
                    {t("components.parcel.markAsStockout.form.action.deselectAll")}
                </Button>}
        </DialogTitle>
        <DialogContent className={classNamesList([`products-selection`, isBottomReached ? "products-selection--scrolled" : ""])}
                       sx={{ width: "550px" }} >
            <Typography>
                {t("components.parcel.markAsStockout.form.description", {
                    parcelId: props.parcel.parcelId.toString(),
                })}
            </Typography>
            <div className={"products-selection__table"}>
                {props.parcel.details.map((detail: ParcelDetail, index: number) =>
                    <ParcelStockoutProductSelectionItem key={`parcel-${props.parcel.parcelId}-detail-${detail.productId}`}
                                                        detail={detail}
                                                        stockoutQuantity={stockoutData.get(detail.productId) ?? 0}
                                                        onQuantityChanged={stockoutQuantity =>
                                                            handleParcelDetailSelected(detail, stockoutQuantity)}
                                                        propertyRef={props.parcel.details.length - 1 === index ?
                                                            bottomItemRef : undefined}/>)}
            </div>
        </DialogContent>
        <DialogActions>
            <Button variant={"outlined"} onClick={props.onCancel}>
                {t("components.parcel.markAsStockout.form.action.cancel")}
            </Button>
            {props.hasNext && <Button variant={"outlined"} onClick={() => props.onParcelCompleted(props.parcel)}>
                {t("components.parcel.markAsStockout.form.action.skip")}
            </Button>}
            <Button onClick={handleStockoutExecute}
                    variant={"contained"}
                    startIcon={<Done />}
                    color={"secondary"}
                    disabled={stockoutData.size === 0}
                    loading={isStockoutRunning}
                    loadingPosition={"start"}>
                {t("components.parcel.markAsStockout.form.action.confirm")}
            </Button>
        </DialogActions>
    </Fragment>;
};

const ParcelStockoutProductSelectionItem = (
    { detail, stockoutQuantity, propertyRef, onQuantityChanged }: {
        propertyRef: RefObject<HTMLDivElement> | undefined,
        detail: ParcelDetail,
        stockoutQuantity: number;
        onQuantityChanged: (stockoutQuantity: number) => void;
    }) => {

    const handleDecrease = useCallback(() => onQuantityChanged(stockoutQuantity > 0 ?
        stockoutQuantity - 1 : 0), []);
    const handleIncrease = useCallback(() => onQuantityChanged(stockoutQuantity !== detail.quantity ?
        stockoutQuantity + 1 : detail.quantity), []);

    return <div ref={propertyRef} className={"products-selection__item"}>
        <Typography className={"products-selection__item-label"}>
            {detail.labelReference}
        </Typography>
        <div className={"quantity-picker"}>
            <Button size={"small"} onClick={handleDecrease} disabled={stockoutQuantity === 0}>
                <Remove />
            </Button>
            <Typography className={"quantity-picker-value"}>
                {stockoutQuantity}
            </Typography>
            <Button size={"small"} onClick={handleIncrease} disabled={stockoutQuantity === detail.quantity}>
                <Add />
            </Button>
        </div>
    </div>;
};
